import { Flex } from "@portal/BaseComponents/Flex/Flex";
import {
  Form,
  FormProvider,
  useForm,
  useFormWatch,
} from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { InvoiceSearchWrapper } from "@portal/BaseComponents/InvoicesSearch/InvoiceSearchWrapper";
import { onSelectedValuesChange } from "@portal/BaseComponents/InvoicesSearch/type";
import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { Icon } from "@portal/lib/icons/icons";
import {
  useCreateBulkPTPQuery,
  useCreatePTPQuery,
} from "@portal/queries/ptp-query";
import { Invoice } from "@portal/types/entities/invoice";
import { CreatePTP, PTPfieldIds } from "@portal/types/entities/ptp";
import { Unpacked } from "@portal/types/utils";
import produce from "immer";
import { filter, subtract, sum } from "lodash";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PTPAmountAttribute,
  PTPDateForm,
  PTPNotesForm,
} from "../PTPFormComponents";

const PTPIcon = (
  <Icon
    icon={["far", "handshake-simple"]}
    color="var(--cyan-6)"
    size="2x"
    width="24px"
  />
);

interface PTPModalProps {
  open: boolean;
  onClose: () => void;
  getUpdatedValues?: (value?: CreatePTP) => void;
  value?: CreatePTP;
  onOpenChange?: (open: false) => void;
  defaultInvoice?: Invoice[];
  loading?: boolean;
}

type AmountErrors = {
  id: string;
  error: boolean;
};
const amountDetailsPredicated = (
  invoice: Invoice
): Unpacked<CreatePTP["amount_details"]> => {
  return {
    invoice_id: invoice.id,
    amount: subtract(
      invoice.balance_due_amount,
      invoice.ptp_balance_amount ?? 0
    ),
  };
};

function PTPModal(props: PTPModalProps) {
  const {
    open,
    onClose,
    value,
    onOpenChange,
    getUpdatedValues,
    defaultInvoice,
  } = props;

  const [form] = useForm<CreatePTP>();

  const amountDetails = useFormWatch(PTPfieldIds.amount_details, form);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice[] | undefined>(
    initializeSelectedInvoice
  );

  function getTotalAmount() {
    if (!amountDetails) return;
    return sum(amountDetails.map((f) => f.amount));
  }

  function initializeSelectedInvoice() {
    return defaultInvoice;
  }
  const [errors, setIsErrors] = useState<Array<AmountErrors>>();

  const createdSinglePTP = useCreatePTPQuery({
    onSuccessCallBack,
    getUpdatedValues,
  });
  const createBulkPtp = useCreateBulkPTPQuery({
    onSuccessCallBack,
    getUpdatedValues,
  });

  function handleClose() {
    onClose();
    setSelectedInvoice(undefined);
    setIsErrors(undefined);
    if (searchParams.has("entity_type")) {
      setSearchParams({}, { state: null, replace: true });
    }
  }

  function onSuccessCallBack() {
    setSelectedInvoice(undefined);
    handleClose();
  }
  async function createPTP(value: CreatePTP) {
    if (!selectedInvoice) return;
    if (selectedInvoice.length > 1) {
      createBulkPtp.mutateAsync(value);

      return;
    }

    createdSinglePTP.mutateAsync(value);
  }
  function handleSubmit() {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      createPTP(values);
    });
  }

  function handleErrorChange(value: string[]) {
    const updatedValues = filter(errors, (error: AmountErrors) => {
      return value.includes(error.id);
    });

    setIsErrors(updatedValues);
  }
  function handleInvoiceSearch(...args: onSelectedValuesChange) {
    const [value, options] = args;

    setSelectedInvoice(options);

    const amountDetails = options?.map(amountDetailsPredicated);
    form.setFieldValue(PTPfieldIds.amount_details, amountDetails);
    handleErrorChange(value);
  }

  function handleAmountChange(invoiceId: string, amount: number) {
    const details = form.getFieldValue(
      PTPfieldIds.amount_details
    ) as CreatePTP["amount_details"];

    const updateValues = produce(details, (draft) => {
      const index = details.findIndex((f) => f.invoice_id === invoiceId);
      draft[index].amount = amount;
      return draft;
    });

    form.setFieldValue(PTPfieldIds.amount_details, updateValues);
  }

  const totalAmount = useMemo(getTotalAmount, [amountDetails]);

  const HeaderTitle = (
    <Flex align="center" justify="start">
      {PTPIcon}
      <TypographyText fs="20">Set Promise to Pay</TypographyText>
    </Flex>
  );

  function getInvoiceIds() {
    return value?.amount_details.map((detail) => detail.invoice_id);
  }

  function handleOpenChange() {
    handleClose();
    onOpenChange?.(false);
  }

  const defaultInvoices = useMemo(getInvoiceIds, [value?.amount_details]);

  const Header = <FormHeader title={HeaderTitle} onClose={handleClose} />;
  const Footer = (
    <FormFooter
      cancelButtonProps={{ onClick: handleClose }}
      saveButtonProps={{
        htmlType: "submit",
        onClick: handleSubmit,
        disabled: errors?.map((f) => f.error).includes(true),
        loading: createdSinglePTP.isLoading || createBulkPtp.isLoading,
      }}
    />
  );
  const AssociatedInvoice = (
    <StyledFormItem
      label="Add Invoices"
      space="sp-1"
      required
      initialValue={
        defaultInvoices || defaultInvoice?.map((invoice) => invoice.id)
      }
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.amount_details}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <InvoiceSearchWrapper
        selectedValuesCallBack={handleInvoiceSearch}
        initialValue={defaultInvoice}
        openInvoicesOnly
      />
    </StyledFormItem>
  );
  const PTPDate = (
    <StyledFormItem
      label="PTP Date"
      space="sp-1"
      required
      initialValue={value?.pay_date}
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.pay_date}
      rules={[{ required: true, message: "This Field is Mandatory" }]}
    >
      <PTPDateForm />
    </StyledFormItem>
  );

  const AmountTable = (
    <StyledFormItem space="sp-1">
      <PTPAmountAttribute
        invoiceList={selectedInvoice}
        onAmountChange={handleAmountChange}
        totalAmount={totalAmount}
      />
    </StyledFormItem>
  );
  const PromissoryNote = (
    <StyledFormItem
      label="Promissory Note"
      space="sp-1"
      style={{ width: "100%" }}
      preserve={false}
      name={PTPfieldIds.description}
    >
      <PTPNotesForm />
    </StyledFormItem>
  );

  const PTPForm = (
    <FormProvider
      onFormChange={(name, info) => {
        if (!name) return;
        const isError = info.forms[name].getFieldError("payment_amount");
        if (errors?.length) {
          const updatedValues = produce(errors, (draft) => {
            const index = draft.findIndex((f) => f.id === name);
            if (index === -1) {
              draft.push({ id: name, error: Boolean(isError.length) });
            } else {
              draft[index].error = Boolean(isError.length);
            }
          });
          setIsErrors(updatedValues);
        } else {
          setIsErrors([{ id: name, error: Boolean(isError.length) }]);
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          amount_details: defaultInvoice?.map(amountDetailsPredicated),
        }}
      >
        {AssociatedInvoice}
        {PTPDate}
        {AmountTable}
        {PromissoryNote}
      </Form>
    </FormProvider>
  );

  return (
    <Modal
      open={open}
      type="activities"
      forceRender={false}
      closable={false}
      title={Header}
      width={640}
      centered
      destroyOnClose
      footer={Footer}
      maskClosable
      onCancel={handleOpenChange}
      style={{ height: "640px" }}
    >
      {PTPForm}
    </Modal>
  );
}

export { PTPModal };
